import React from "react";
import { Box, Divider, Stack } from "@mui/material";
import { formatCurrency, formatTokenAmount, formatUsdOrDash, USD_CENTS_BELOW } from "../../utils/formatters";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { IStats } from "../../shared/types";
import DetailItem from "../../components/cards/subcomponents/DetailItem";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { Metric } from "../../components/ui/Metric";

interface Props {
  stats: IStats;
  decimals: number;
  isAdmin: boolean;
}

/** Summary amounts: whole dollars from $100 up, cents below; zero reads "—". */
const usd = formatUsdOrDash;

/**
 * Largest-remainder rounding: rounds `values` to whole `unit`s that add up to `total` rounded the same
 * way (the figure shown above them). Each value goes to its floor or its ceiling, and the units left
 * over go to the largest remainders. Values that don't reconcile with the total (off by more than one
 * unit per row, e.g. an older payload) are rounded to their own sum instead of being bent to fit.
 */
const roundToTotal = (values: number[], total: number, unit: number): number[] => {
  // Scaled to units, trimmed to 6 decimals so float noise (12.34 / 0.01 = 1233.9999…) doesn't floor a whole unit away.
  const scaled = values.map((v) => (Number.isFinite(v) ? Math.round((v / unit) * 1e6) / 1e6 : 0));
  const floors = scaled.map((v) => Math.floor(v));
  const floorSum = floors.reduce((a, b) => a + b, 0);
  let left = Math.round(total / unit) - floorSum;
  if (!(left >= 0 && left <= values.length)) left = Math.round(scaled.reduce((a, b) => a + b, 0)) - floorSum;
  const rounded = [...floors];
  scaled
    .map((v, i) => ({ i, rest: v - floors[i] }))
    .sort((a, b) => b.rest - a.rest)
    .slice(0, left)
    .forEach(({ i }) => {
      rounded[i] += 1;
    });
  return rounded.map((n) => n * unit);
};

/** USD price of one token, as the pair prices read: "$2,648.31", "$151.27"; 4 significant digits below $1 ("$0.6118"). */
const formatUsdPrice = (value: number): string => {
  if (!value || !Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 1) return formatCurrency(value, 2);
  return `$${value.toLocaleString("en-US", { minimumSignificantDigits: 4, maximumSignificantDigits: 4 })}`;
};

const TotalCapitalCard: React.FC<Props> = ({ stats }) => {
  // The USD value the backend adds to total capital (tokens bought × their USD price). Never derived from
  // currentPrice: that is the pair price (WETH per FET), so 18,420 FET would read $4.
  const buybacksValue = stats.repurchaseCurValue || 0;
  const hasBuybacks = (stats.repurchaseAmount || 0) > 0 || buybacksValue > 0;
  const balancing = stats.balancingUSD || 0;
  // Uncollected fees of the open position: the bot already counts them in the pool value
  // (liquidity + fees), so they are a breakdown of the Pool row, never an addend.
  const accruedFees = stats.accruedFeesUSD || 0;

  // Total capital = wallet + pool + balancing + buybacks value. Lent is part of balancing and
  // uncollected fees part of the pool (sub-rows, not addends).
  const rows = [
    { label: "Wallet", value: stats.balanceSnapshotEnd.walletValueUSD || 0, shown: true },
    { label: "Pool", value: stats.balanceSnapshotEnd.poolValueUSD || 0, shown: true },
    { label: "Balancing", value: balancing, shown: balancing > 0 },
    // Hidden for bots that buy nothing back; "—" while bought tokens have no USD value from the backend.
    { label: "Buybacks value", value: buybacksValue, shown: hasBuybacks },
  ].filter((row) => row.shown);

  // The rows are rounded together so they add up to the total as displayed ($402,500, not $402,501),
  // at the total's precision: whole dollars from $100 up (as usd() shows it), cents below.
  const total = stats.totalCapitalUSD || 0;
  const unit = Math.abs(total) >= USD_CENTS_BELOW ? 1 : 0.01;
  const rounded = roundToTotal(
    rows.map((row) => row.value),
    total,
    unit
  );
  const shown = (label: string) => rounded[rows.findIndex((row) => row.label === label)] ?? 0;
  const fmt = (value: number) => (value ? formatCurrency(value, unit === 1 ? 0 : 2) : "—");
  // Never more than the row each one belongs to.
  const lent = Math.min(Math.round((stats.lentUSD || 0) / unit) * unit, shown("Balancing"));
  const uncollected = Math.min(Math.round(accruedFees / unit) * unit, shown("Pool"));

  return (
    <SurfaceCard tone="accent" sx={{ height: "100%" }}>
      <Metric
        icon={AccountBalanceWalletRoundedIcon}
        label="Total capital"
        value={usd(stats.totalCapitalUSD)}
        size="xl"
      />
      <Divider sx={{ my: 2 }} />
      <Stack spacing={0.25}>
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <DetailItem
              label={row.label}
              value={fmt(shown(row.label))}
              sections={
                row.label === "Buybacks value"
                  ? [
                      {
                        title: "Buybacks value",
                        items: [
                          {
                            label: "Tokens bought",
                            // No guessed unit when the payload doesn't name the token.
                            value: [formatTokenAmount(stats.repurchaseAmount), stats.repurchaseToken].filter(Boolean).join(" "),
                          },
                          { label: "Current price", value: formatUsdPrice(stats.repurchaseCurPrice) },
                          { label: "Value", value: fmt(shown("Buybacks value")) },
                        ],
                      },
                    ]
                  : undefined
              }
            />
            {row.label === "Pool" && uncollected > 0 && (
              <Box sx={{ pl: 1.5 }}>
                <DetailItem label="of which uncollected fees" value={fmt(uncollected)} valueColor="text.secondary" />
              </Box>
            )}
            {/* Lending parks part of the balancing surplus: a breakdown of the row above, outside the sum. */}
            {row.label === "Balancing" && lent > 0 && (
              <Box sx={{ pl: 1.5 }}>
                <DetailItem label="of which lent" value={fmt(lent)} valueColor="text.secondary" />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </SurfaceCard>
  );
};

export default TotalCapitalCard;
